import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie
} from 'recharts';
import { 
  FiCpu, 
  FiHardDrive, 
  FiActivity, 
  FiServer,
  FiWifi,
  FiZap
} from 'react-icons/fi';
import { fetchRuns } from '../services/api';
import MetricInfoButton from './MetricInfoButton';

const SystemResourcesOverview = () => {
  const [data, setData] = useState([]);
  const [latestRun, setLatestRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSystemMetrics = async () => {
      try {
        const runsResponse = await fetchRuns();
        // Handle both cases: when fetchRuns returns an array directly or an object with runs property
        const runs = Array.isArray(runsResponse) ? runsResponse : (runsResponse?.runs || []);
        
        if (runs.length === 0) {
          setError('No system metrics data available');
          return;
        }

        // Get the latest run with system metrics
        const latestRunWithMetrics = runs.find(run => 
          run.system_metrics && typeof run.system_metrics === 'object'
        );

        if (!latestRunWithMetrics || !latestRunWithMetrics.system_metrics) {
          setError('No system metrics found in recent runs');
          return;
        }

        // Extract metrics from the latest run
        const metrics = latestRunWithMetrics.system_metrics;
        
        // Calculate disk usage percentage with better validation
        const diskTotal = metrics.disk_total_space_bytes || 1;
        const diskUsed = metrics.disk_used_space_bytes || 0;
        let diskUsagePercent = 0;
        
        if (diskTotal > 0 && diskUsed >= 0) {
          diskUsagePercent = Math.round((diskUsed / diskTotal) * 100);
          // Ensure percentage doesn't exceed 100%
          diskUsagePercent = Math.min(diskUsagePercent, 100);
        }
        
        // Calculate memory usage percentage with better validation
        let memoryUsagePercent = 0;
        const memoryUtilization = metrics['otel::system.memory.utilization'] || 0;
        
        if (memoryUtilization >= 0) {
          memoryUsagePercent = Math.round(memoryUtilization * 100);
          // Ensure percentage doesn't exceed 100%
          memoryUsagePercent = Math.min(memoryUsagePercent, 100);
        }
        
        // Calculate process memory usage percentage
        const processMemoryInUse = metrics.process_memory_in_use || metrics['otel::process.memory.usage'] || 0;
        const processMemoryAvailable = metrics.process_memory_available || 0;
        
        // Try different calculation methods for process memory
        let processMemoryPercent = 0;
        
        // Method 1: If we have both in_use and available
        if (processMemoryInUse > 0 && processMemoryAvailable > 0) {
          processMemoryPercent = Math.round((processMemoryInUse / (processMemoryInUse + processMemoryAvailable)) * 100);
        }
        // Method 2: If we only have in_use, assume it's a percentage of system memory
        else if (processMemoryInUse > 0 && metrics['otel::system.memory.usage'] > 0) {
          const systemMemoryUsage = metrics['otel::system.memory.usage'];
          processMemoryPercent = Math.round((processMemoryInUse / systemMemoryUsage) * 100);
        }
        // Method 3: Check if there's a direct utilization metric
        else if (metrics['otel::process.memory.utilization']) {
          processMemoryPercent = Math.round(metrics['otel::process.memory.utilization'] * 100);
        }
        
        // Only include metrics that have valid data (non-zero values)
        const processedData = [];
        
        // CPU metric - only show if there's actual data
        const cpuUsage = Math.round((metrics['otel::system.cpu.utilization'] || 0) * 100);
        if (cpuUsage > 0) {
          processedData.push({ name: 'CPU', usage: cpuUsage });
        }
        
        // Memory metric - only show if there's actual data and it's a reasonable percentage
        if (memoryUsagePercent > 0 && memoryUsagePercent <= 100) {
          processedData.push({ 
            name: 'Memory', 
            usage: memoryUsagePercent,
            value: memoryUsagePercent, // Explicit value for chart
            actualGB: metrics['otel::system.memory.usage'] ? (metrics['otel::system.memory.usage'] / 1024 / 1024 / 1024).toFixed(2) : null
          });
        }
        
        // Storage metric - only show if there's actual data and it's a reasonable percentage
        if (diskUsagePercent > 0 && diskUsagePercent <= 100) {
          processedData.push({ 
            name: 'Storage', 
            usage: diskUsagePercent,
            value: diskUsagePercent, // Explicit value for chart
            actualGB: diskTotal > 0 ? (diskTotal / 1024 / 1024 / 1024).toFixed(2) : null,
            usedGB: diskUsed > 0 ? (diskUsed / 1024 / 1024 / 1024).toFixed(2) : null
          });
        }
        
        // Process CPU metric - only show if there's actual data
        const processCpuUsage = Math.round((metrics['otel::process.cpu.utilization'] || metrics.process_cpu_utilization_percent || 0) * 100);
        if (processCpuUsage > 0) {
          processedData.push({ name: 'Process CPU', usage: processCpuUsage });
        }
        
        // Process Memory metric - show if we have any process memory data
        if (processMemoryInUse > 0) {
          // Use the actual calculated percentage, ensure it's reasonable
          let displayUsage = processMemoryPercent > 0 ? processMemoryPercent : 15;
          // Ensure percentage doesn't exceed 100%
          displayUsage = Math.min(displayUsage, 100);
          
          processedData.push({ 
            name: 'Process Memory', 
            usage: displayUsage,
            value: displayUsage, // Explicit value for chart
            actualUsage: processMemoryPercent,
            actualMB: (processMemoryInUse / 1024 / 1024).toFixed(2),
            hasPercentage: processMemoryPercent > 0
          });
        }

        // If no valid data found, show message instead of empty chart
        if (processedData.length === 0) {
          setError('No valid system metrics available in the latest run');
          return;
        }

        setData(processedData);
        setLatestRun(latestRunWithMetrics);
      } catch (err) {
        setError('Failed to load system metrics: ' + err.message);
        console.error('Error loading system metrics:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSystemMetrics();
  }, []);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const getDetailedInfo = (label, value, data) => {
        if (!latestRun || !latestRun.system_metrics) return null;
        
        const metrics = latestRun.system_metrics;
        switch(label) {
          case 'CPU':
            return `System: ${value}%`;
          case 'Memory':
            const memUsed = (metrics['otel::system.memory.usage'] / 1024 / 1024 / 1024).toFixed(2);
            return `${value}% (${memUsed} GB used)`;
          case 'Storage':
            const diskUsed = (metrics.disk_used_space_bytes / 1024 / 1024 / 1024).toFixed(2);
            const diskTotal = (metrics.disk_total_space_bytes / 1024 / 1024 / 1024).toFixed(2);
            return `${value}% (${diskUsed} GB / ${diskTotal} GB)`;
          case 'Process CPU':
            return `Process: ${value}%`;
          case 'Process Memory':
            // If we have actual usage data, show it
            if (data && data.actualMB) {
              if (data.hasPercentage) {
                const actualPercent = data.actualUsage !== undefined ? data.actualUsage : value;
                return `${data.actualMB} MB (${actualPercent}%)`;
              } else {
                return `${data.actualMB} MB`;
              }
            }
            const procMem = (metrics.process_memory_in_use / 1024 / 1024).toFixed(2);
            return `${procMem} MB`;
          default:
            return `${value}%`;
        }
      };
      
      const itemData = payload[0].payload;
      const metricColor = getBarColor(label, itemData.usage);
      
      return (
        <div className="bg-white p-3 shadow-md rounded-md border border-gray-100">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: metricColor }} />
          <p className="font-medium text-sm font-inter">{label}</p>
          </div>
          <p className="text-xs text-gray-700 font-inter">
            {getDetailedInfo(label, payload[0].value, itemData) || `${payload[0].value}% utilization`}
          </p>
        </div>
      );
    }
    return null;
  };

  const getBarColor = (name, usage) => {
    // Different colors for each metric type - matching ResourceUtilization
    const metricColors = {
      'CPU': {
        low: '#3B82F6',      // Blue
        medium: '#2563EB',   // Darker Blue
        high: '#1D4ED8'      // Even Darker Blue
      },
      'Memory': {
        low: '#10B981',      // Green
        medium: '#059669',   // Darker Green
        high: '#047857'      // Even Darker Green
      },
      'Storage': {
        low: '#8B5CF6',      // Purple
        medium: '#7C3AED',   // Darker Purple
        high: '#6D28D9'      // Even Darker Purple
      },
      'Process CPU': {
        low: '#F59E0B',      // Amber
        medium: '#D97706',   // Darker Amber
        high: '#B45309'      // Even Darker Amber
      },
      'Process Memory': {
        low: '#EC4899',      // Pink
        medium: '#DB2777',   // Darker Pink
        high: '#BE185D'      // Even Darker Pink
      },
      'Network': {
        low: '#14B8A6',      // Teal
        medium: '#0D9488',   // Darker Teal
        high: '#0F766E'      // Even Darker Teal
      },
      'GPU': {
        low: '#EF4444',      // Red
        medium: '#DC2626',   // Darker Red
        high: '#B91C1C'      // Even Darker Red
      }
    };

    const colors = metricColors[name] || metricColors['CPU'];
    
    if (usage > 80) return colors.high;
    if (usage > 60) return colors.medium;
    return colors.low;
  };



  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white -m-6 p-6 shadow-sm mb-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-600 text-sm mt-1">System performance metrics</p>
            </div>
            <Link 
              to="/" 
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading system metrics...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white -m-6 p-6 shadow-sm mb-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-600 text-sm mt-1">System performance metrics</p>
            </div>
            <Link 
              to="/" 
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-center h-64">
            <div className="text-red-500">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to get detailed metrics
  const getDetailedMetrics = () => {
    if (!latestRun || !latestRun.system_metrics) return null;
    const metrics = latestRun.system_metrics;
    
    return {
      cpu: {
        system: Math.round((metrics['otel::system.cpu.utilization'] || 0) * 100),
        process: Math.round((metrics['otel::process.cpu.utilization'] || metrics.process_cpu_utilization_percent || 0) * 100),
        threads: metrics.process_cpu_threads_in_use || 0
      },
      memory: {
        system: Math.round((metrics['otel::system.memory.utilization'] || 0) * 100),
        systemUsed: (metrics['otel::system.memory.usage'] / 1024 / 1024 / 1024).toFixed(2),
        process: (metrics.process_memory_in_use / 1024 / 1024).toFixed(1),
        available: (metrics.process_memory_available / 1024 / 1024 / 1024).toFixed(1)
      },
      disk: {
        total: (metrics.disk_total_space_bytes / 1024 / 1024 / 1024).toFixed(0),
        used: (metrics.disk_used_space_bytes / 1024 / 1024 / 1024).toFixed(0),
        free: (metrics.disk_free_space_bytes / 1024 / 1024 / 1024).toFixed(0),
        percentage: Math.round((metrics.disk_used_space_bytes / metrics.disk_total_space_bytes) * 100)
      },
      network: {
        io: metrics['otel::system.network.io'] || 0,
        packets: metrics['otel::system.network.packets'] || 0,
        errors: metrics['otel::system.network.errors'] || 0
      },
      latency: {
        llm: (metrics['otel::llm_latency::sum'] || 0).toFixed(0),
        tokens: metrics['otel::tokens_total'] || 0
      }
    };
  };

  const detailedMetrics = getDetailedMetrics();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white -m-6 p-6 shadow-sm mb-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-gray-600 text-sm mt-1">Real-time system performance metrics</p>
          </div>
          <Link 
            to="/" 
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Metric Cards Grid */}
      {detailedMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* CPU Card - Only show if CPU usage > 0 */}
          {detailedMetrics.cpu.system > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FiCpu className="text-blue-600" size={20} />
                  <h3 className="text-lg font-semibold text-gray-800">CPU Usage</h3>
                </div>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  detailedMetrics.cpu.system > 80 ? 'bg-red-100' : 
                  detailedMetrics.cpu.system > 60 ? 'bg-yellow-100' : 'bg-green-100'
                }`}>
                  <span className="text-sm font-bold">
                    {detailedMetrics.cpu.system}%
                  </span>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${
                      detailedMetrics.cpu.system > 80 ? 'bg-red-500' : 
                      detailedMetrics.cpu.system > 60 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${detailedMetrics.cpu.system}%` }}
                  />
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">System CPU:</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-gray-100 rounded-full h-1.5">
                      <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${detailedMetrics.cpu.system}%` }} />
                    </div>
                    <span className="font-medium text-xs">{detailedMetrics.cpu.system}%</span>
                  </div>
                </div>
                {detailedMetrics.cpu.process > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Process CPU:</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-gray-100 rounded-full h-1.5">
                        <div className="bg-indigo-500 h-1.5 rounded-full" style={{ width: `${detailedMetrics.cpu.process}%` }} />
                      </div>
                      <span className="font-medium text-xs">{detailedMetrics.cpu.process}%</span>
                    </div>
                  </div>
                )}
                {detailedMetrics.cpu.threads > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Threads:</span>
                    <span className="font-medium">{detailedMetrics.cpu.threads}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Memory Card - Only show if memory usage > 0 */}
          {detailedMetrics.memory.system > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FiServer className="text-green-600" size={20} />
                  <h3 className="text-lg font-semibold text-gray-800">Memory</h3>
                </div>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                detailedMetrics.memory.system > 80 ? 'bg-red-100' : 
                detailedMetrics.memory.system > 60 ? 'bg-yellow-100' : 'bg-green-100'
              }`}>
                <span className="text-sm font-bold">
                  {detailedMetrics.memory.system}%
                </span>
              </div>
            </div>
            
            {/* Circular Progress */}
            <div className="flex justify-center mb-4">
              <div className="relative">
                <img src="/images/svg/circular-progress.svg" alt="Progress" className="w-24 h-24 transform -rotate-90" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Used</div>
                    <div className="text-sm font-bold">{detailedMetrics.memory.systemUsed}GB</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Used:</span>
                <span className="font-medium">{detailedMetrics.memory.systemUsed} GB</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Process:</span>
                <span className="font-medium">{detailedMetrics.memory.process} MB</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Available:</span>
                <span className="font-medium">{detailedMetrics.memory.available} GB</span>
              </div>
            </div>
          </div>
          )}
          
          {/* Disk Card - Only show if disk usage > 0 */}
          {detailedMetrics.disk.percentage > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FiHardDrive className="text-purple-600" size={20} />
                  <h3 className="text-lg font-semibold text-gray-800">Storage</h3>
                </div>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  detailedMetrics.disk.percentage > 80 ? 'bg-red-100' : 
                  detailedMetrics.disk.percentage > 60 ? 'bg-yellow-100' : 'bg-green-100'
                }`}>
                  <span className="text-sm font-bold">
                    {detailedMetrics.disk.percentage}%
                  </span>
                </div>
              </div>
              
              {/* Pie Chart */}
              <div className="h-32 mb-2">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Used', value: parseInt(detailedMetrics.disk.used), fill: '#8B5CF6' },
                        { name: 'Free', value: parseInt(detailedMetrics.disk.free), fill: '#E5E7EB' }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={50}
                      startAngle={90}
                      endAngle={450}
                      dataKey="value"
                    >
                      <Cell fill="#8B5CF6" />
                      <Cell fill="#E5E7EB" />
                    </Pie>
                    <Tooltip formatter={(value) => `${value} GB`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-2 text-sm">
                {detailedMetrics.disk.used > 0 && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                      <span className="text-gray-600">Used:</span>
                    </div>
                    <span className="font-medium">{detailedMetrics.disk.used} GB</span>
                  </div>
                )}
                {detailedMetrics.disk.free > 0 && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-gray-200 rounded-full"></div>
                      <span className="text-gray-600">Free:</span>
                    </div>
                    <span className="font-medium">{detailedMetrics.disk.free} GB</span>
                  </div>
                )}
                {detailedMetrics.disk.total > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total:</span>
                    <span className="font-medium">{detailedMetrics.disk.total} GB</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Network & Performance Card - Only show if there's performance data */}
          {(detailedMetrics.network?.bytesSent > 0 || detailedMetrics.network?.bytesRecv > 0 || detailedMetrics.performance?.loadAverage > 0) && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FiActivity className="text-orange-600" size={20} />
                  <h3 className="text-lg font-semibold text-gray-800">Performance</h3>
                </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <FiZap className="text-blue-600" size={20} />
              </div>
            </div>
            
            {/* Performance Metrics with Icons */}
            <div className="space-y-3">
              <div className="p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">LLM Latency</span>
                  <span className="text-sm font-bold text-orange-600">{detailedMetrics.latency.llm}ms</span>
                </div>
                <div className="w-full bg-orange-100 rounded-full h-1.5">
                  <div 
                    className="bg-orange-500 h-1.5 rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min((detailedMetrics.latency.llm / 5000) * 100, 100)}%` }}
                  />
                </div>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">Tokens Processed</span>
                  <span className="text-sm font-bold text-blue-600">{detailedMetrics.latency.tokens}</span>
                </div>
                <div className="flex items-center gap-1 mt-1">
                  {[...Array(5)].map((_, i) => (
                    <div 
                      key={i}
                      className={`h-2 w-full rounded-sm ${
                        i < Math.ceil((detailedMetrics.latency.tokens / 100) * 5) 
                          ? 'bg-blue-500' 
                          : 'bg-blue-100'
                      }`}
                    />
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FiWifi className="text-gray-500" size={14} />
                  <span className="text-xs text-gray-600">Network I/O:</span>
                </div>
                <span className="text-sm font-medium">{detailedMetrics.network.io}B</span>
              </div>
            </div>
          </div>
        )}
        </div>
      )}



            {/* Overall System Health Visualization */}
      {detailedMetrics && data.length > 0 && (
        <div className="space-y-6">
          {/* First Row - Two Equal Sized Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Radial Bar Chart Card */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-medium text-gray-800">Resource Usage Summary</h3>
                <MetricInfoButton metricType="system-metrics" />
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getBarColor(entry.name, entry.usage)} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`${value}%`, name]}
                      labelFormatter={(label) => `${label}`}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-3 mt-4">
                {data.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: getBarColor(item.name, item.usage) }}
                    />
                    <span className="text-gray-600">{item.name}: {item.usage}%</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Bar Chart Comparison Card */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-medium text-gray-800">Resource Comparison</h3>
                <MetricInfoButton metricType="system-metrics" />
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F6" />
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false}
                      tick={{ fontSize: 10, fill: '#64748B' }}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                      tick={{ fontSize: 10, fill: '#64748B' }}
                  domain={[0, 100]}
                />
                <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="usage" radius={[8, 8, 0, 0]}>
                  {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getBarColor(entry.name, entry.usage)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
          
          {/* Second Row - Two Smaller Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* System Status Card */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-medium text-gray-800">System Status</h3>
                  <MetricInfoButton metricType="system-metrics" />
                </div>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  data.every(item => item.usage < 60) ? 'bg-green-100' :
                  data.some(item => item.usage > 80) ? 'bg-red-100' : 'bg-yellow-100'
                }`}>
                  <div className={`w-6 h-6 rounded-full ${
                    data.every(item => item.usage < 60) ? 'bg-green-500' :
                    data.some(item => item.usage > 80) ? 'bg-red-500' : 'bg-yellow-500'
                  }`} />
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Overall Health</span>
                  <span className={`text-sm font-semibold ${
                    data.every(item => item.usage < 60) ? 'text-green-600' :
                    data.some(item => item.usage > 80) ? 'text-red-600' : 'text-yellow-600'
                  }`}>
                    {data.every(item => item.usage < 60) ? 'Healthy' :
                     data.some(item => item.usage > 80) ? 'Critical' : 'Warning'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Resources Above 80%</span>
                  <span className="text-sm font-semibold text-gray-800">
                    {data.filter(item => item.usage > 80).length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Resources</span>
                  <span className="text-sm font-semibold text-gray-800">{data.length}</span>
                </div>
              </div>
            </div>
            
            {/* Performance Metrics Card */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-medium text-gray-800">Performance Metrics</h3>
                <MetricInfoButton metricType="performance" />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Average Usage</span>
                  <span className="text-sm font-semibold text-gray-800">
                    {Math.round(data.reduce((sum, item) => sum + item.usage, 0) / data.length)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Highest Usage</span>
                  <span className="text-sm font-semibold text-gray-800">
                    {Math.max(...data.map(item => item.usage))}% ({data.find(item => item.usage === Math.max(...data.map(d => d.usage)))?.name})
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Lowest Usage</span>
                  <span className="text-sm font-semibold text-gray-800">
                    {Math.min(...data.map(item => item.usage))}% ({data.find(item => item.usage === Math.min(...data.map(d => d.usage)))?.name})
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemResourcesOverview; 